import { DynamicFormComponent } from '../../../dynamic-form/dynamic-form.component';
import { WorkflowClass } from '@modules/campaigns/Domain/WorkflowClass';
import { NodeClass } from '@modules/campaigns/Domain/NodeClass';
import { ParseInfoFormToClassService } from '@shared/components/joint-js/services/parseInfoFormToClass.service';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ToastService } from '@services/ui';
import { ActivatedRoute } from '@angular/router';
import { mapFilterCopies } from '@shared/components/joint-js/helpers';
import { CAMPAIGN_TYPE, CampaignModel } from '@modules/campaigns/models/campaign.model';
import { TranslateService } from '@ngx-translate/core';
import { NodeType } from '@modules/campaigns/Domain/Enums/NodeType';
import { GetCopyService } from '@shared/components/joint-js/services/get-copies';
import { finalize, of, Subject } from 'rxjs';
import {
  ActionBranch,
  ConditionalConfig,
  MakeOutBoundCallConfig,
  RetryTimeRange,
} from '@modules/campaigns/Domain/Interfaces/INodeConfig';
import { catchError } from 'rxjs/operators';
import { NodeFormService } from '@modules/campaigns/Application/Services/NodeForm';
import { MenuItem } from 'primeng/api';
import { hascopyIds } from '@modules/campaigns/Domain/Factories/HasNodeConfig';
import { SegmentationService } from '@modules/segments/services/segments.service';
import { TreeNode } from '@shared/components/tree-select/Domain/treeNode.model';
import { OrderCampaignsService } from '@shared/services/orderCampaigns.service';
import { DialogService } from 'primeng/dynamicdialog';
import { MakeoutboundcallService } from '@shared/components/joint-js/services/makeoutboundcall.service';

@Component({
  selector: 'app-node-inspector',
  templateUrl: './node-inspector.component.html',
  styleUrls: ['./node-inspector.component.scss'],
})
export class NodeInspectorComponent implements OnChanges {
  @Input() isEditable?: boolean = true;
  @Input() workflow?: WorkflowClass;
  @Input() selectedNode?: NodeClass;
  @Input() selectedGoalNode?: NodeClass;
  @Output() nodeChange = new EventEmitter<{ id: string; newAttributes: any }>();
  @Output() nodeGoalToSelect = new EventEmitter<any>();
  @Output() zoomOnNode = new EventEmitter<{ x: number; y: number }>();
  @Output() deleteGoalNode = new EventEmitter<any>();

  @ViewChild('nodeForm') nodeForm?: DynamicFormComponent;
  router = inject(ActivatedRoute);
  public formType: any;
  public copies = [];
  public allowedCopies: {} = [];
  public campaigns = [] as CampaignModel[];
  public segments: [] = [];
  public segmentsList: {} = [];
  public agentIds: { label: string; value: string }[] = [];
  public conditionalParentsNodes: {} = [];
  public loading = false;

  campaignDataFilters: TreeNode[] = [];
  items: MenuItem[] | undefined;
  option = 'minimumAbsoluteAmount';

  copiesMessage: {
    header: string;
    text: string;
    asunto: string;
    buttonText: string;
    typeChanel: string;
    preHeader: string;
  }[] = [];
  nameGoalNode: NodeType | string = '';

  private selectedNodeSubject = new Subject<NodeClass>();

  constructor(
    public nodeFormService: NodeFormService,
    public dialogService: DialogService,
    private toast: ToastService,
    private translate: TranslateService,
    private copyService: GetCopyService,
    private segmentService: SegmentationService,
    private orderCampaigns: OrderCampaignsService,
  ) {
    this.getData();
    this.selectedNodeSubject.subscribe((node) => {
      this.getGoalNodeConditional(node);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedGoalNode']) {
      this.selectedNodeSubject.next(this.selectedNode!);
    }

    if (changes['selectedNode'] && this.selectedNode) {
      this.handleInfoNewNode();

      this.selectedNodeSubject.next(changes['selectedNode'].currentValue);

      let waitDays = this.selectedNode.config['waitDays'];

      if (
        this.selectedNode.type == NodeType.WaitDaysForDueDate &&
        Number(this.selectedNode.config['waitDays']) < 0
      ) {
        this.selectedNode.config['waitDays'] = -1 * this.selectedNode.config['waitDays'];
      }

      if (
        this.selectedNode.type == NodeType.MakeOutBoundCall &&
        this.selectedNode.config['retryTimeRanges']?.length
      ) {
        this.retryTimeRanges = this.selectedNode!.config['retryTimeRanges'];
      }

      if (
        this.selectedNode.type == NodeType.MakeOutBoundCall &&
        this.selectedNode.config['actions']?.length
      ) {
        this.actions = this.selectedNode!.config['actions'];
      }

      this.formType = this.nodeFormService.generateFormControls(
        this.selectedNode?.config!,
        this.allowedCopies,
        this.segmentsList,
        this.agentIds,
      );

      if (this.selectedNode.type == NodeType.WaitDaysForDueDate && waitDays && waitDays < 0) {
        this.formType[2].value = 'beforeDueDate';
      }
    }
  }

  handleInfoNewNode(): void {
    this.handleCopiesList({
      type: this.selectedNode?.type!,
      copyIds: this.copies,
    });
    this.handleParentsConditionalNode();
    this.handleSegmentsList();
  }

  getData() {
    this.router.data.subscribe(
      (data) => {
        this.copies = data['copiesListResolver'];
        this.campaigns = data['campaignListResolver'];
        this.segments = data['segments'];
        this.agentIds = data['agentIds']
          .sort((a: any, b: any) => a.name.localeCompare(b.name))
          .map((agent: any) => ({
            label: agent.name,
            value: agent.id,
          }));

        this.handleCampaignDataFilter();
      },
      (error) => {
        this.toast.showError('Error getting data', error.message);
      },
    );

    if (!this.segments) {
      this.getSegments();
    }
  }

  getSelectedCampaignKey(): string[] {
    if (
      this.selectedNode?.type === NodeType.AssignCampaign &&
      this.selectedNode.config['targetCampaignId']
    ) {
      return [this.selectedNode.config['targetCampaignId']];
    }
    return [];
  }

  handleCampaignDataFilter() {
    this.campaignDataFilters = this.orderCampaigns.parseCampaignsToTree(this.campaigns);
  }

  onCampaignSelect(selectedNodes: TreeNode[]) {
    if (!this.selectedNode || !selectedNodes.length) return;

    const selectedCampaign = selectedNodes[0];

    const newAttributes = {
      campañaId: selectedCampaign.key,
    };

    this.updateNodeAttributes(newAttributes);
  }

  getSegments() {
    this.segmentService.getSegments().subscribe((segments) => {
      this.segments = segments;
    });
  }

  addGoalNode() {
    if (this.selectedNode?.parentNodes && this.selectedNode?.parentNodes.length > 0) {
      this.nodeGoalToSelect.emit();
    } else {
      this.toast.showInfo(
        'Nodo no conectado',
        'El Nodo debe estar conectado para encontrar su nodo objetivo',
      );
    }
  }

  updateNodeAttributes(newAttributes: any) {
    try {
      if (this.selectedNode) {
        const currentCopyIds = this.selectedNode.config?.['copyIds'] || [];

        const rawCopyIds = newAttributes?.copyIds;
        const newCopyIds = Array.isArray(rawCopyIds)
          ? rawCopyIds
          : typeof rawCopyIds === 'string'
            ? [rawCopyIds]
            : [];
        const copyIdsChanged = this.haveCopyIdsChanged(currentCopyIds, newCopyIds);

        ParseInfoFormToClassService.ParseInfoFormToClass(
          newAttributes,
          this.workflow!,
          this.selectedNode,
        );
        this.selectedNode.config['retryTimeRanges'] = this.retryTimeRanges;

        if (copyIdsChanged) {
          this.getPlantillas();
        }
      }
    } catch (error) {
      this.toast.showError('Error mapping info node', (error as Error).message);
    }
  }

  haveCopyIdsChanged(currentCopyIds: string[], newCopyIds: string[]): boolean {
    if (currentCopyIds.length !== newCopyIds.length) {
      return true;
    }
    return currentCopyIds.some((id, index) => id !== newCopyIds[index]);
  }

  handleCopiesList(values: { type: string; copyIds: string[] }) {
    this.allowedCopies = this.copies
      .filter((copy: any) => copy.type === mapFilterCopies(values.type))
      .map((copy: any) => ({
        label: copy.name,
        value: copy.id,
      }));

    this.getPlantillas();
  }

  getPlantillas(): void {
    if (this.allowedCopies && this.selectedNode) {
      const config = this.selectedNode.config;
      if (hascopyIds(config) && config.copyIds !== null) {
        this.getCopyMessage(config.copyIds, this.selectedNode.type);
      } else {
        this.copiesMessage = [];
      }
    }
  }

  handleSegmentsList() {
    if (this.segments && this.segments.length > 0) {
      this.segmentsList = this.segments.map((campaign: any) => ({
        label: campaign.name,
        value: campaign.id,
      }));
    }
  }

  handleParentsConditionalNode() {
    if (!this.selectedNode || this.selectedNode.type !== NodeType.Conditional) {
      return;
    }
    const ancestors = this.workflow?.getParentNodes(this.selectedNode) || [];
    if (ancestors.length > 0) {
      this.conditionalParentsNodes = ancestors
        .filter((node: any) => {
          return (
            node.type === NodeType.SendCertifiedEmail ||
            node.type === NodeType.SendCertifiedSMS ||
            node.type === NodeType.SendSMS ||
            node.type === NodeType.SendEmail ||
            node.type === NodeType.SuperSendCertifiedSMS ||
            node.type === NodeType.SuperSendCertifiedEmail
          );
        })
        .map((node: any, index: number) => {
          return {
            label: `${index + 1} - ${this.translate.instant(
              'DATA.MODULES.CAMPAINGS.ELEMENTS.NODES.PARAMETERS.NAME.' + node.type,
            )}`,
            value: node.id,
          };
        });
    }
  }

  truncateText(text: string): string {
    const maxLength = 30;
    if (text.length > maxLength) {
      return text.substr(0, maxLength - 3) + '...';
    } else {
      return text;
    }
  }

  copyText(text: string) {
    navigator.clipboard.writeText(text).then(() => {
      this.toast.showInfo('Copiado', 'Mensaje copiado en el portapapeles');
    });
  }

  getCopyMessage(ids: string[], type: string) {
    this.loading = true;
    this.copiesMessage = [];

    if (ids && ids.length > 0) {
      ids.forEach((id, index) => {
        this.copyService
          .getCopy(id)
          .pipe(
            finalize(() => {
              if (index === ids.length - 1) {
                this.loading = false;
              }
            }),
            catchError((error) => {
              this.toast.showError(
                'Error',
                `Fallo al cargar plantilla de mensaje: ${error.message}`,
              );
              return of(null);
            }),
          )
          .subscribe((data: any) => {
            if (data && data.errors) {
              this.toast.showError('Error', `${data.errors[0].message}`);
              return;
            }
            let text: string = '';
            let header: string = '';
            let asunto: string = '';
            let buttonText: string = '';
            let typeChanel: string = '';
            let preHeader: string = '';

            if (type.includes('SMS') || type.includes('sms')) {
              text = data.data.copy.blocks[0].translations[0].text || '';
              asunto = data.data.copy.name;
              typeChanel = 'SMS';
            } else {
              asunto = data.data.copy.blocks[0].translations[0].text || '';
              text = data.data.copy.blocks[2].translations[0].text || '';
              preHeader = data.data.copy.blocks[1].translations[0].text || '';
              buttonText = data.data.copy.blocks[3].translations[0].text || '';
              typeChanel = 'EMAIL';
            }
            header = data.data.copy.name || '';

            const existingIndex = this.copiesMessage.findIndex((entry) => entry.header === header);
            if (existingIndex !== -1) {
              this.copiesMessage.splice(existingIndex, 1);
            }
            this.copiesMessage.push({ header, text, asunto, buttonText, typeChanel, preHeader });
          });
      });
    }
  }

  getGoalNodeConditional(node: NodeClass) {
    if (node && (node?.config as ConditionalConfig).targetNodeId) {
      const nodeFinded = this.workflow?.findNode((node?.config as ConditionalConfig).targetNodeId!);

      this.nameGoalNode = nodeFinded ? nodeFinded.type : 'No se ha encontrado nodo';
    } else if (
      node &&
      ((node?.config as ConditionalConfig).targetNodeId === null ||
        (node?.config as ConditionalConfig).targetNodeId === undefined)
    ) {
      this.nameGoalNode = '';
    }
  }

  retryTimeRanges: RetryTimeRange[] = [
    {
      label: 'DATA.MODULES.CAMPAINGS.ELEMENTS.NODES.PARAMETERS.RETRY',
      from: '09:00',
      to: '12:00',
      selected: false,
    },
    {
      label: 'DATA.MODULES.CAMPAINGS.ELEMENTS.NODES.PARAMETERS.RETRY',
      from: '12:00',
      to: '15:00',
      selected: false,
    },
    {
      label: 'DATA.MODULES.CAMPAINGS.ELEMENTS.NODES.PARAMETERS.RETRY',
      from: '15:00',
      to: '19:00',
      selected: false,
    },
  ];

  onRetryRangeToggle(newValue: boolean, index: number) {
    this.retryTimeRanges[index].selected = newValue;

    const config = this.selectedNode?.config as MakeOutBoundCallConfig;
    if (!config) return;

    // Keep full retryTimeRanges structure (even unchecked ones)
    config.retryTimeRanges = [...this.retryTimeRanges];

    this.workflow!.updateNode(this.selectedNode!);
  }

  onRetryRangeTimeChange(index: number, field: 'from' | 'to', value: Date) {
    const current = this.retryTimeRanges[index];
    // returns HH:mm
    current[field] = this.getTimeValue(value);

    // Reset all errors
    this.retryTimeRanges.forEach((r) => (r.errors = {}));

    // Skip validation if current range is not selected or incomplete
    if (!current.selected || !current.from || !current.to) {
      this.updateRetryRangesInNode();
      return;
    }

    const fromTime = this.getTimeValue(current.from);
    const toTime = this.getTimeValue(current.to);

    // Validate current range order
    if (fromTime >= toTime) {
      current.errors = { ...current.errors, invalidOrder: true };
    }

    // Check for overlaps with other selected ranges
    for (let i = 0; i < this.retryTimeRanges.length; i++) {
      if (i === index) continue;

      const other = this.retryTimeRanges[i];
      if (!other.selected || !other.from || !other.to) continue;

      const otherFrom = this.getTimeValue(other.from);
      const otherTo = this.getTimeValue(other.to);

      if (fromTime < otherTo && toTime > otherFrom) {
        current.errors = { ...current.errors, overlaps: true };
        break;
      }
    }

    this.updateRetryRangesInNode();
  }

  getTimeValue(value: Date | string): string {
    let date: Date;

    if (typeof value === 'string') {
      const [hours, minutes] = value.split(':').map(Number);
      date = new Date();
      date.setHours(hours, minutes, 0, 0);
    } else {
      date = value;
    }

    const h = date.getHours().toString().padStart(2, '0');
    const m = date.getMinutes().toString().padStart(2, '0');
    return `${h}:${m}`;
  }

  private updateRetryRangesInNode(): void {
    const config = this.selectedNode?.config as MakeOutBoundCallConfig;
    if (!config) return;

    config.retryTimeRanges = [...this.retryTimeRanges];
    this.workflow!.updateNode(this.selectedNode!);
  }

  actions: ActionBranch[] = [];
  actionOptions = [
    { label: 'Feedback', value: 'endFeedback' },
    { label: 'Asignar campaña', value: 'assignCampaign' },
  ];

  newAction: Partial<ActionBranch> = {
    situation: '',
    action: 'feedback',
    campaign: '',
  };
  showAddActionForm = false;

  addAction() {
    if (!this.newAction.situation?.trim() || !this.newAction.action) return;

    const action: ActionBranch = {
      situation: this.newAction.situation.trim(),
      action: this.newAction.action,
    };

    if (this.newAction.action === 'assignCampaign' && this.newAction.campaign) {
      action.campaign = this.newAction.campaign;
    }

    this.actions.push(action);
    this.syncActionsToNode();

    // Reset form
    this.newAction = {
      situation: '',
      action: 'feedback',
      campaign: '', // also reset selected campaigns
    };
    this.showAddActionForm = false;
  }

  removeAction(index: number) {
    this.actions.splice(index, 1);
    this.syncActionsToNode();
  }

  syncActionsToNode() {
    const config = this.selectedNode?.config as MakeOutBoundCallConfig;
    if (!config) return;

    config.actions = [...this.actions];
  }

  getActionLabel(action: string, campaignId?: string): string {
    if (action === 'assignCampaign' && campaignId) {
      const campaign = this.campaigns.find((c) => c.id === campaignId);
      return campaign ? `Asignar a ${campaign.name}` : 'Asignar a [Campaña no encontrada]';
    }

    return this.actionOptions.find((o) => o.value === action)?.label || action;
  }

  protected readonly NodeType = NodeType;
  protected readonly hascopyIds = hascopyIds;
  protected readonly CAMPAIGN_TYPE = CAMPAIGN_TYPE;

  onSelectedNodeChange($event: NodeClass) {
    this.selectedNode = $event;
  }

  onCampaignTreeSelect(selectedNodes: TreeNode[]) {
    if (selectedNodes.length > 0) {
      const selected = selectedNodes[0];
      this.newAction.campaign = selected.key;
    } else {
      this.newAction.campaign = undefined;
    }
  }
}
